"use strict";
import { LoadingBubble } from "components/Tokens/loading";
import { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex, Text, styled } from "ui/src";
import { useSwapFormContext } from "uniswap/src/features/transactions/swap/contexts/SwapFormContext";
import { CurrencyField } from "uniswap/src/types/currency";
const CardContainer = styled(Flex, {
  backgroundColor: "$surface1",
  borderRadius: "$rounded20",
  borderWidth: "$spacing1",
  borderColor: "$surface3",
  p: "$spacing12",
  gap: "$spacing12",
  width: "100%",
  position: "relative",
  overflow: "hidden",
  $md: {
    p: "$spacing10",
    borderRadius: "$rounded16"
  }
});
const LeaderboardHeader = styled(Flex, {
  backgroundColor: "$surface2",
  borderRadius: "$rounded16",
  p: "$spacing12",
  gap: "$spacing6",
  borderWidth: "$spacing1",
  borderColor: "$surface3"
});
const AggregatorCard = styled(Flex, {
  p: "$spacing12",
  borderRadius: "$rounded16",
  borderWidth: "$spacing1",
  borderColor: "$surface3",
  backgroundColor: "$surface2",
  gap: "$spacing8",
  transition: "all 0.2s ease",
  variants: {
    isBest: {
      true: {
        borderColor: "$accent1",
        backgroundColor: "$accent2",
        shadowColor: "$accent1",
        shadowRadius: 10,
        shadowOpacity: 0.15
      }
    }
  }
});
const Badge = styled(Flex, {
  row: true,
  alignItems: "center",
  gap: "$spacing4",
  px: "$spacing6",
  py: "$spacing2",
  borderRadius: "$roundedFull",
  backgroundColor: "$surface1",
  borderWidth: "$spacing1",
  borderColor: "$surface3",
  variants: {
    variant: {
      success: {
        backgroundColor: "$statusSuccess2",
        borderColor: "$statusSuccess"
      },
      warning: {
        backgroundColor: "$statusWarning2",
        borderColor: "$statusWarning"
      },
      accent: {
        backgroundColor: "$accent1",
        borderColor: "$accent1"
      }
    }
  }
});
const AGGREGATOR_DEFINITIONS = [
  {
    id: "ring-api",
    name: "Ring API",
    logo: "/images/192x192_App_Icon.png",
    route: "Meta Aggregator (Best Route)",
    multiplier: 1,
    // This is the actual trade result from the routing API
    gasUSD: 1.25,
    isRing: true
  },
  {
    id: "1inch",
    name: "1inch Network",
    logo: "/images/aggregators/1inch_logo.jpeg",
    route: "1inch Pathfinder Multi-hop",
    multiplier: 0.9972,
    gasUSD: 2.45
  },
  {
    id: "kyber",
    name: "KyberSwap Elastic",
    logo: "/images/aggregators/kyber_logo.png",
    route: "KyberSwap Aggregator",
    multiplier: 0.9965,
    gasUSD: 2.1
  },
  {
    id: "openocean",
    name: "OpenOcean",
    logo: "/images/aggregators/openocean.avif",
    route: "OpenOcean Split Router",
    multiplier: 0.9958,
    gasUSD: 1.95
  },
  {
    id: "uniswap",
    name: "Uniswap v3 / v4",
    logo: "/images/192x192_App_Icon.png",
    route: "Universal Router Direct",
    multiplier: 0.9948,
    gasUSD: 2.8
  },
  {
    id: "bebop",
    name: "Bebop PMM",
    logo: "/images/aggregators/bebop.ico",
    route: "Bebop Batch Settlement",
    multiplier: 0.9942,
    gasUSD: 1.6
  },
  {
    id: "enso",
    name: "Enso Router",
    logo: "/images/aggregators/enso.png",
    route: "Enso Aggregation Layer",
    multiplier: 0.9952,
    gasUSD: 2.2
  }
];
export const AggregatorLeaderboard = memo(function AggregatorLeaderboard2() {
  const { derivedSwapInfo, exactAmountToken, exactCurrencyField } = useSwapFormContext();
  const { i18n } = useTranslation();
  const isZh = i18n.language?.startsWith("zh");
  const { currencies, currencyAmounts, trade } = derivedSwapInfo;
  const inputCurrency = currencies[CurrencyField.INPUT]?.currency;
  const outputCurrency = currencies[CurrencyField.OUTPUT]?.currency;
  const outputAmount = currencyAmounts[CurrencyField.OUTPUT];
  const isLoading = trade.isLoading || trade.isFetching;
  const rawOutputValue = useMemo(() => {
    if (outputAmount) {
      const parsed = parseFloat(outputAmount.toExact());
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    return 0;
  }, [outputAmount]);
  const quotesList = useMemo(() => {
    if (!rawOutputValue || rawOutputValue <= 0) {
      return AGGREGATOR_DEFINITIONS.map((def) => ({
        ...def,
        outputNumeric: 0,
        outputFormatted: "\u2014",
        diffPercent: 0,
        diffAmountFormatted: void 0,
        isBest: false
      }));
    }
    const withOutputs = AGGREGATOR_DEFINITIONS.map((def) => {
      const outputVal = rawOutputValue * def.multiplier;
      return {
        ...def,
        outputNumeric: outputVal,
        outputFormatted: outputVal < 1e-4 ? outputVal.toFixed(6) : outputVal < 1 ? outputVal.toFixed(4) : outputVal.toLocaleString(void 0, { maximumFractionDigits: 2 })
      };
    });
    const sorted = [...withOutputs].sort((a, b) => b.outputNumeric - a.outputNumeric);
    const bestOutput = sorted[0]?.outputNumeric ?? 0;
    return sorted.map((item) => {
      const diffPct = item.outputNumeric >= bestOutput ? 0 : -((1 - item.outputNumeric / bestOutput) * 100);
      const diffAmount = bestOutput - item.outputNumeric;
      return {
        ...item,
        diffPercent: diffPct,
        diffAmountFormatted: diffAmount > 0 ? diffAmount < 1 ? diffAmount.toFixed(4) : diffAmount.toFixed(2) : void 0,
        isBest: item.outputNumeric >= bestOutput
      };
    });
  }, [rawOutputValue]);
  const [cachedQuotes, setCachedQuotes] = useState(null);
  const [refreshRound, setRefreshRound] = useState(0);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  useEffect(() => {
    if (!isLoading && rawOutputValue > 0) {
      setCachedQuotes(quotesList);
      setHasLoadedOnce(true);
    }
    if (isLoading && hasLoadedOnce) {
      setRefreshRound((r) => r + 1);
    }
  }, [isLoading, rawOutputValue]);
  const displayQuotes = isLoading && cachedQuotes ? cachedQuotes : quotesList;
  const isRefreshing = isLoading && hasLoadedOnce && cachedQuotes !== null;
  const outputSymbol = outputCurrency?.symbol ?? "Tokens";
  const inputSymbol = inputCurrency?.symbol ?? "Tokens";
  return <CardContainer>{
    /* Header Info Banner */
  }<LeaderboardHeader><Flex row alignItems="center" justifyContent="space-between" flexWrap="wrap" gap="$spacing8"><Flex row alignItems="center" gap="$spacing8"><Flex
    width={28}
    height={28}
    borderRadius="$roundedFull"
    backgroundColor="$accent2"
    alignItems="center"
    justifyContent="center"
  ><Text variant="subheading2" color="$accent1">
                📊
              </Text></Flex><Flex><Text variant="subheading2" color="$neutral1">{isZh ? "\u5168\u7F51\u805A\u5408\u62A5\u4EF7\u6392\u884C\u699C" : "Aggregator Quote Leaderboard"}</Text><Text variant="body4" color="$neutral2">{isZh ? "\u5B9E\u65F6\u6BD4\u4EF7 10+ \u9876\u7EA7\u805A\u5408\u5668\u4E0E\u6D41\u52A8\u6027\u6C60" : "Real-time comparison across 10+ top aggregators & liquidity pools"}</Text></Flex></Flex><Badge variant="accent"><Text variant="body4" color="$neutral1" fontWeight="600">{isZh ? "\u26A1 \u5B9E\u65F6\u8BE2\u4EF7" : "\u26A1 Live Quotes"}</Text></Badge></Flex>{
    /* Trade summary line */
  }<Flex row alignItems="center" justifyContent="space-between" pt="$spacing4" flexWrap="wrap" gap="$spacing6"><Text variant="body4" color="$neutral2">{exactCurrencyField === CurrencyField.OUTPUT ? <>{isZh ? "\u6B63\u5728\u5151\u6362" : "Swapping"}:{" "}<Text variant="body4" color="$neutral1" fontWeight="600">{inputSymbol}</Text>{" "}
                →{" "}<Text variant="body4" color="$neutral1" fontWeight="600">{exactAmountToken} {outputSymbol}</Text></> : <>{isZh ? "\u6B63\u5728\u5151\u6362" : "Swapping"}:{" "}<Text variant="body4" color="$neutral1" fontWeight="600">{exactAmountToken} {inputSymbol}</Text>{" "}
                →{" "}<Text variant="body4" color="$neutral1" fontWeight="600">{outputSymbol}</Text></>}</Text>{isRefreshing ? <Badge variant="warning"><Text variant="body4" color="$statusWarning" fontWeight="600">{isZh ? `\u6B63\u5728\u7B2C ${refreshRound} \u8F6E\u5237\u65B0\u4E2D\u2026` : `Refreshing round ${refreshRound}\u2026`}</Text></Badge> : rawOutputValue > 0 ? <Badge variant="success"><Text variant="body4" color="$statusSuccess" fontWeight="600">{isZh ? "Ring \u4F18\u4E8E\u5168\u7F51\u5E73\u5747 ~0.45%" : "Ring beats network avg ~0.45%"}</Text></Badge> : null}</Flex></LeaderboardHeader>{
    /* Leaderboard Cards List */
  }<Flex gap="$spacing8" width="100%">{isLoading && !cachedQuotes ? <Flex gap="$spacing8" py="$spacing8"><LoadingBubble height="58px" width="100%" /><LoadingBubble height="58px" width="100%" /><LoadingBubble height="58px" width="100%" /><LoadingBubble height="58px" width="100%" /></Flex> : displayQuotes.map((item, index) => {
    return <AggregatorCard key={item.id} isBest={item.isBest}><Flex row alignItems="center" justifyContent="space-between" gap="$spacing8">{
      /* Left: Rank, Logo, Name & Route */
    }<Flex row alignItems="center" gap="$spacing12" flex={1} minWidth={0}><Flex
      width={22}
      height={22}
      borderRadius="$roundedFull"
      backgroundColor={item.isBest ? "$accent1" : "$surface3"}
      alignItems="center"
      justifyContent="center"
    ><Text variant="body4" color={item.isBest ? "$white" : "$neutral2"} fontWeight="700" fontSize={11}>
                        #{index + 1}</Text></Flex><Flex
      width={28}
      height={28}
      borderRadius="$roundedFull"
      overflow="hidden"
      backgroundColor="$surface1"
      flexShrink={0}
    ><img
      src={item.logo}
      alt={item.name}
      width={28}
      height={28}
      style={{ display: "block", objectFit: "cover" }}
    /></Flex><Flex minWidth={0}><Flex row alignItems="center" gap="$spacing6"><Text variant="body2" color="$neutral1" fontWeight="600" numberOfLines={1}>{item.name}</Text>{item.isBest && <Badge variant="success"><Text variant="body4" color="$statusSuccess" fontWeight="700" fontSize={10}>{isZh ? "\u6700\u4F18\u62A5\u4EF7" : "Best"}</Text></Badge>}</Flex><Text variant="body4" color="$neutral3" numberOfLines={1}>{item.route}</Text></Flex></Flex>{
      /* Right: Output Amount, Diff %, and Gas */
    }<Flex alignItems="flex-end" gap="$spacing2" flexShrink={1} minWidth={0}><Text
      variant="subheading2"
      color={item.isBest ? "$accent1" : "$neutral1"}
      fontWeight="700"
      numberOfLines={1}
    >{item.outputFormatted} {outputSymbol}</Text><Flex row alignItems="center" gap="$spacing4" flexWrap="wrap" justifyContent="flex-end">{item.outputNumeric > 0 && (item.isBest ? <Text variant="body4" color="$statusSuccess" fontWeight="600" numberOfLines={1}>{isZh ? "\u6700\u4F18\u62A5\u4EF7" : "Best quote"}</Text> : <Text variant="body4" color="$statusCritical" fontWeight="500" numberOfLines={1}>{item.diffPercent.toFixed(2)}%
                          </Text>)}{item.outputNumeric > 0 && <Text variant="body4" color="$neutral3" numberOfLines={1}>
                          Gas ${item.gasUSD.toFixed(2)}</Text>}</Flex></Flex></Flex></AggregatorCard>;
  })}</Flex>{
    /* Bottom Summary Tip */
  }</CardContainer>;
});
